import React, { useState, useEffect, useCallback } from 'react';
import { Tabs, Button, Space, Spin, Empty } from '@arco-design/web-react';
import { IconPlus } from '@arco-design/web-react/icon';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import classNames from 'classnames';
import { RootState } from '../../store/store';
import axios from 'axios';
import { endpoints } from '../../api/endpoints';
import { Event } from '../../models/Event';
import { EventCard } from '../../components/EventCard/EventCard';
import { EventForm } from '../../components/EventForm/EventForm';
import { currentThemeName } from '../../theme';
import '../../components/PillTabs/pill-tabs.css';
import './Events.css';

const TabPane = Tabs.TabPane;

type EventFilter = 'all' | 'upcoming' | 'past';

const Events: React.FC = () => {
  const { t } = useTranslation();
  const user = useSelector((state: RootState) => state.user.user);
  const isAdmin = user?.isAdmin || false;

  const [activeTab, setActiveTab] = useState<EventFilter>('all');
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(false);
  const [showEventForm, setShowEventForm] = useState(false);
  const [editingEvent, setEditingEvent] = useState<Event | null>(null);

  const fetchEvents = useCallback(async (filter: EventFilter) => {
    setLoading(true);
    try {
      const response = await axios.get(endpoints.getEvents(filter));
      setEvents(response.data || []);
    } catch (error) {
      console.error('Error fetching events:', error);
      setEvents([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchEvents(activeTab);
  }, [activeTab, fetchEvents]);

  const handleTabChange = (key: string) => {
    setActiveTab(key as EventFilter);
  };

  const handleCreateEvent = () => {
    setEditingEvent(null);
    setShowEventForm(true);
  };

  const handleEditEvent = (event: Event) => {
    setEditingEvent(event);
    setShowEventForm(true);
  };

  const handleFormClose = () => {
    setShowEventForm(false);
    setEditingEvent(null);
  };

  const handleFormSuccess = () => {
    fetchEvents(activeTab);
  };

  return (
    <div className={classNames("events-page", {
      "events-page--black-theme": currentThemeName === 'black'
    })}>
      <div className="events-page__header">
        <h1 className="events-page__title">{t('events.page_title')}</h1>
        {isAdmin && (
          <Button
            type="primary"
            shape="round"
            icon={<IconPlus />}
            onClick={handleCreateEvent}
            className={classNames("events-page__create-button", {
              "events-page__create-button--black-theme": currentThemeName === 'black'
            })}
          >
            {t('events.create_event')}
          </Button>
        )}
      </div>

      <Tabs
        activeTab={activeTab}
        onChange={handleTabChange}
        type="rounded"
        className={classNames("events-page__tabs pill-tabs", {
          "pill-tabs--black-theme": currentThemeName === "black",
        })}
      >
        <TabPane key="all" title={t('events.all')}>
          {loading ? (
            <div className="events-page__loading">
              <Spin size={32} />
            </div>
          ) : events.length === 0 ? (
            <Empty description={t('events.no_events')} />
          ) : (
            <div className="events-page__list">
              {events.map((event) => (
                <EventCard
                  key={event.id}
                  event={event}
                  onUpdate={handleFormSuccess}
                  onEdit={handleEditEvent}
                />
              ))}
            </div>
          )}
        </TabPane>

        <TabPane key="upcoming" title={t('events.upcoming')}>
          {loading ? (
            <div className="events-page__loading">
              <Spin size={32} />
            </div>
          ) : events.length === 0 ? (
            <Empty description={t('events.no_upcoming_events')} />
          ) : (
            <div className="events-page__list">
              {events.map((event) => (
                <EventCard
                  key={event.id}
                  event={event}
                  onUpdate={handleFormSuccess}
                  onEdit={handleEditEvent}
                />
              ))}
            </div>
          )}
        </TabPane>

        <TabPane key="past" title={t('events.past')}>
          {loading ? (
            <div className="events-page__loading">
              <Spin size={32} />
            </div>
          ) : events.length === 0 ? (
            <Empty description={t('events.no_past_events')} />
          ) : (
            <div className="events-page__list">
              {events.map((event) => (
                <EventCard
                  key={event.id}
                  event={event}
                  onUpdate={handleFormSuccess}
                  onEdit={handleEditEvent}
                />
              ))}
            </div>
          )}
        </TabPane>
      </Tabs>

      {showEventForm && (
        <EventForm
          visible={showEventForm}
          event={editingEvent}
          onClose={handleFormClose}
          onSuccess={handleFormSuccess}
        />
      )}
    </div>
  );
};

export default Events;

